import { ILineSelectionInfo, ICallerIdOption } from './types';
import { getMultiLineStyle } from '../../packages/widgets/src/utils/handleMultiLineLabel';

interface LineSelectorItemProps {
  line: ILineSelectionInfo;
  focusedId: string | null;
  onFocusChange: (id: string | null) => void;
  onSelectLine: (callerIdIndex?: number) => void | Promise<void>;
}

/**
 * Helper function to navigate between list items with arrow keys.
 * Skips group headers (role="presentation") when navigating.
 */
function handleArrowNavigation(e: React.KeyboardEvent<HTMLElement>, currentElement: HTMLElement) {
  const parent = currentElement.parentElement;
  if (!parent) return;

  // Get all selectable items (those with role="option")
  const selectableItems = Array.from(parent.querySelectorAll('[role="option"]')) as HTMLElement[];
  const currentIndex = selectableItems.indexOf(currentElement);
  if (currentIndex === -1) return;

  let nextIndex = -1;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      nextIndex = Math.min(currentIndex + 1, selectableItems.length - 1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      nextIndex = Math.max(currentIndex - 1, 0);
      break;
    case 'Home':
      e.preventDefault();
      nextIndex = 0;
      break;
    case 'End':
      e.preventDefault();
      nextIndex = selectableItems.length - 1;
      break;
    default:
      return;
  }

  if (nextIndex !== -1 && nextIndex !== currentIndex) {
    selectableItems[nextIndex]?.focus();
  }
}

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="line-selector__item-check">
    <path d="M13.3536 4.35355C13.5488 4.15829 13.5488 3.84171 13.3536 3.64645C13.1583 3.45118 12.8417 3.45118 12.6464 3.64645L6 10.2929L3.35355 7.64645C3.15829 7.45118 2.84171 7.45118 2.64645 7.64645C2.45118 7.84171 2.45118 8.15829 2.64645 8.35355L5.64645 11.3536C5.84171 11.5488 6.15829 11.5488 6.35355 11.3536L13.3536 4.35355Z"
      fill="var(--mds-color-theme-text-accent-normal)" fillOpacity="0.95" />
  </svg>
);

/**
 * Presentational component for a single line option.
 * Uses plain HTML for full control — no ListItemBase/FocusRing overhead.
 *
 * When a line has callerIds[], renders:
 *   - A non-interactive group header (badge + line name)
 *   - Indented caller ID sub-items (each selectable)
 *
 * When a line has no callerIds, renders a single selectable row (badge + name + number).
 */
export const LineSelectorItem = ({ line, focusedId, onFocusChange, onSelectLine }: LineSelectorItemProps) => {
  const badgeStyle = getMultiLineStyle(line.lineLabel);

  // Lines with multiple caller IDs — render as group header + sub-items
  if (line.callerIds && line.callerIds.length > 0) {
    return (
      <>
        <li className="line-selector__group-header" role="presentation" aria-hidden="true">
          <span className="line-selector__item-badge" style={badgeStyle}>
            {line.lineLabel}
          </span>
          <span className="line-selector__item-title">{line.title}</span>
        </li>
        {line.callerIds.map((cid) => {
          const itemId = `${line.lineIndex}-cid-${cid.callerIdIndex}`;
          const isFocused = focusedId === itemId;
          return (
            <li
              key={itemId}
              id={itemId}
              className={`line-selector__item line-selector__item--sub ${cid.isSelected ? 'line-selector__item--selected' : ''} ${isFocused ? 'line-selector__item--focused' : ''}`}
              role="option"
              aria-selected={cid.isSelected}
              aria-label={`${line.lineLabel} ${cid.name} ${cid.number}`}
              onClick={() => onSelectLine(cid.callerIdIndex)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectLine(cid.callerIdIndex);
                } else {
                  handleArrowNavigation(e, e.currentTarget);
                }
              }}
              onFocus={() => onFocusChange(itemId)}
              onBlur={() => onFocusChange(null)}
              tabIndex={isFocused ? 0 : -1}
              data-testid={`line-selector-cid-${itemId}`}
            >
              <div className="line-selector__item-content">
                <span className="line-selector__item-label">
                  <span className="line-selector__item-title">{cid.name}</span>
                  <span className="line-selector__item-number">{cid.number}</span>
                </span>
                {cid.isSelected && <CheckIcon />}
              </div>
            </li>
          );
        })}
      </>
    );
  }

  // Simple line — single selectable row
  const itemId = `line-${line.lineIndex}`;
  const isFocused = focusedId === itemId;
  return (
    <li
      id={itemId}
      className={`line-selector__item ${line.isSelected ? 'line-selector__item--selected' : ''} ${isFocused ? 'line-selector__item--focused' : ''}`}
      role="option"
      aria-selected={line.isSelected}
      aria-label={`${line.lineLabel} ${line.title} ${line.number || ''}`}
      onClick={() => onSelectLine()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelectLine();
        } else {
          handleArrowNavigation(e, e.currentTarget);
        }
      }}
      onFocus={() => onFocusChange(itemId)}
      onBlur={() => onFocusChange(null)}
      tabIndex={isFocused ? 0 : -1}
      data-testid={`line-selector-item-${line.lineIndex}`}
    >
      <div className="line-selector__item-content">
        <span className="line-selector__item-badge" style={badgeStyle}>
          {line.lineLabel}
        </span>
        <span className="line-selector__item-label">
          <span className="line-selector__item-title">{line.title}</span>
          {line.number && (
            <span className="line-selector__item-number">{line.number}</span>
          )}
        </span>
        {line.isSelected && <CheckIcon />}
      </div>
    </li>
  );
};
